import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import bannerImg from "../assets/images/shutterstock_2597873713.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faRulerCombined,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import { motion } from "framer-motion";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import { buttonAnimation, fadeUp, stagger } from "../functions/animations";
import Contact from "./contact.component";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";

function toDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    if (value.highlight != null) return toDisplayText(value.highlight);
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    if (Array.isArray(value.content)) {
      return value.content.map(toDisplayText).filter(Boolean).join(" ");
    }
    for (const nested of Object.values(value)) {
      const text = toDisplayText(nested);
      if (text) return text;
    }
  }
  return "";
}

function toImageSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.image || null;
  }
  return null;
}

function normalizeTestimonialsList(rawTestimonials) {
  let list;
  if (Array.isArray(rawTestimonials)) {
    list = rawTestimonials;
  } else if (typeof rawTestimonials === "string") {
    try {
      const parsed = JSON.parse(rawTestimonials);
      return normalizeTestimonialsList(parsed);
    } catch {
      return [];
    }
  } else if (rawTestimonials && typeof rawTestimonials === "object") {
    if (Array.isArray(rawTestimonials.items)) {
      list = rawTestimonials.items;
    } else if (Array.isArray(rawTestimonials.data)) {
      list = rawTestimonials.data;
    } else {
      list = Object.values(rawTestimonials).filter(
        (item) => item != null && typeof item !== "string"
      );
    }
  } else {
    return [];
  }

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        return {
          name: `Testimonial ${index + 1}`,
          quote: item,
          image: null,
          location: "",
        };
      }
      if (typeof item !== "object") return null;

      const quote = toDisplayText(
        item.quote ?? item.description ?? item.highlight ?? item.text
      );
      const name =
        toDisplayText(item.name) || `Testimonial ${index + 1}`;
      const location = toDisplayText(
        item.location ?? item.role ?? item.title
      );

      return {
        name,
        quote,
        image: toImageSrc(item.image ?? item.img),
        location,
      };
    })
    .filter((item) => item && (item.quote || item.name));
}

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const loaded = useRef(false);
  const {
    getCurrentAgentName,
    getCurrentAbout,
    getCurrentHeadshot,
    getCurrentTestimonials,
  } = useUserParams();

  const rawTestimonials = getCurrentTestimonials();
  const testimonials = useMemo(
    () => normalizeTestimonialsList(rawTestimonials),
    [rawTestimonials]
  );

  // State
  // const [listings, setListings] = useState([]); //for production
  const [listings, setListings] = useState(data.value); //for local testing
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListOfficeKey eq '${constants.OFFICE_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListOfficeKey(S)eq(S)\'${constants.OFFICE_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "local") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `/odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get("userId"),
    };
  };

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(
        `https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templateSettings = await response.json();
      console.log("Template settings fetched:", templateSettings);

      if (templateSettings) {
        const settingsData = templateSettings.data || templateSettings;

        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          ...settingsData,
        };

        globalState.setUserData(mappedUserData);

        console.log("Template settings data:", settingsData);
        console.log("Template settings mapped to user data:", mappedUserData);
      }

      return templateSettings;
    } catch (error) {
      console.error("Error fetching template settings:", error);
      return null;
    }
  };

  // Load template settings when userId is present in URL
  useEffect(() => {
    const urlParams = getURLParameters();
    if (urlParams.userId) {
      globalState.setUserId(urlParams.userId);
      fetchTemplateSettings(urlParams.userId).then(() => {
        console.log(
          "Global State Updated with Template Settings:",
          globalState.getDebugInfo()
        );
      });
      console.log("Global State Updated:", globalState.getDebugInfo());
    }
  }, []);

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />

      {/* Hero Section */}
      <section className="relative bg-[#F8FAFC] pt-28 pb-24 overflow-hidden">
        <div className="mx-auto w-full px-5 lg:px-8 xl:px-16">
          <div className="relative">
            <div className="absolute inset-0 bg-harbour-navy/20 rounded-3xl" />
            {/* Image */}
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <img
                src={bannerImg}
                alt="Luxury Home"
                className="h-[780px] w-full object-cover"
              />
            </div>

            {/* Floating Content Card */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="absolute m-5 bottom-0 right-0 md:m-0 md:bottom-16 md:right-16 md:translate-x-0 md:translate-y-0 max-w-xl rounded-3xl bg-white py-8 px-6 md:p-10 shadow-xl"
            >
              <div className="flex items-center gap-4">
                <div className="h-px w-14 bg-harbour-accent" />
                <span className="uppercase tracking-[0.4em] text-xs text-harbour-blue font-semibold">
                  Trusted Real Estate
                </span>
              </div>
              <h1 className="mt-8 font-serif text-5xl lg:text-7xl leading-[0.95] text-harbour-navy">
                Helping You
                <br />
                Find Home.
              </h1>
              <p className="mt-8 text-lg leading-8 text-harbour-muted">
                From first homes to luxury estates, discover a
                personalized real estate experience built on local
                expertise, trusted guidance, and exceptional results.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/listings"
                  className="btn-harbour"
                >
                  Explore Listings
                </Link>

                <Link
                  to="/contact"
                  className="btn-harbour-outline"
                >
                  Contact
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section >

      {/* About Section */}
      <section className="bg-harbour-navy py-24 overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px w-14 bg-harbour-accent" />
                <span className="uppercase tracking-[0.4em] font-medium text-harbour-sky">
                  About
                </span>
              </div>

              <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] text-harbour-sky">
                {getCurrentAgentName()}
              </h2>

              <p className="mt-8 text-lg leading-8 text-harbour-sky whitespace-pre-line">
                {getCurrentAbout()}
              </p>

              <Link
                to="/about"
                className="inline-flex mt-10 btn-harbour-outline-light"
              >
                Discover More
              </Link>

              <div className="mt-14 grid grid-cols-2 gap-8 border-t border-gray-200 pt-8">
                <div>
                  <p className="font-serif text-4xl text-harbour-sky">15+</p>
                  <span className="text-sm uppercase tracking-widest text-harbour-sky">
                    Years Experience
                  </span>
                </div>

                <div>
                  <p className="font-serif text-4xl text-harbour-sky">250+</p>
                  <span className="text-sm uppercase tracking-widest text-harbour-sky">
                    Homes Sold
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 relative"
            >
              <div className="relative">
                <img
                  src={getCurrentHeadshot()}
                  alt={getCurrentAgentName()}
                  className="h-[650px] w-full rounded-[40px] object-cover"
                />

                <div className="absolute bottom-0 left-0 m-4 md:bottom-8 md:left-8 md:m-0 rounded-3xl bg-white/90 backdrop-blur-xl px-8 py-6 shadow-xl">
                  <p className="font-serif text-3xl text-harbour-navy">
                    Trusted Guidance
                  </p>
                  <p className="mt-2 text-sm uppercase tracking-[0.2em] text-harbour-muted">
                    Every Step Of The Way
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Feature Listings Section */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="mx-auto max-w-[1600px] px-6 py-24"
      >
        <div className="mb-14">
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-5">
            <div className="h-px w-14 bg-harbour-accent" />
            <p className="uppercase tracking-[0.4em] font-medium text-harbour-blue">
              Curated Properties
            </p>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="font-serif text-5xl lg:text-6xl leading-none text-harbour-navy"
          >
            Featured Listings
          </motion.h2>
        </div>

        <motion.div
          variants={fadeUp}
        >
          <Swiper
            modules={[Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            loop
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              1280: {
                slidesPerView: 2,
              },
            }}
          >
            {listings?.slice(0, 6).map((prop) => (
              <SwiperSlide key={prop.ListingKey}>
                <Link
                  to={`/listing/${prop.ListingKey}`}
                  className="group block"
                >
                  <div className="grid overflow-hidden rounded-3xl bg-white border border-harbour-sky md:grid-cols-5">

                    <div className="relative h-[400px] overflow-hidden md:col-span-3 md:h-[420px]">
                      <img
                        // src={
                        //   formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ??
                        //   formatted[prop.ListingKey]?.[0]?.MediaURL ??
                        //   placeholder
                        // }
                        src={prop.MainImgUrl} //dummy image
                        alt={`${prop.StreetNumber} ${prop.StreetName}`}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />

                      <div className="absolute left-5 top-5 rounded-full bg-white px-5 py-2 text-sm text-harbour-navy shadow-md">
                        ${prop.ListPrice?.toLocaleString()}
                      </div>
                    </div>

                    <div className="flex flex-col justify-between p-8 md:col-span-2">
                      <div>
                        <p className="flex items-center text-xs uppercase tracking-[0.3em] text-harbour-blue">
                          <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                          {prop.CityRegion ?? prop.CrossStreet}
                        </p>

                        <h3 className="mt-4 font-serif text-3xl leading-tight text-harbour-navy">
                          {prop.StreetNumber} {prop.StreetName} {prop.StreetSuffix}
                        </h3>

                        <div className="mt-4 h-px bg-gray-200" />

                        <div className="mt-4 space-y-4 text-sm text-harbour-muted">

                          {prop.PropertyType !== "Commercial" && (
                            <>
                              <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={faBed} className="text-harbour-accent" />
                                <span>
                                  {prop.BedroomsTotal || prop.BedroomsTotalInteger} Bedrooms
                                </span>
                              </div>

                              <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={faBath} className="text-harbour-accent" />
                                <span>
                                  {prop.BathroomsTotalInteger} Bathrooms
                                </span>
                              </div>
                            </>
                          )}

                          <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={faRulerCombined} className="text-harbour-accent" />
                            <span>
                              {prop.LivingAreaRange ?? "--"} sq.ft.
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
                        <span className="text-xs uppercase tracking-[0.3em] text-harbour-blue">
                          View Property
                        </span>
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-harbour-navy text-white transition group-hover:bg-harbour-blue">
                          <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        <motion.div variants={buttonAnimation} className="mt-14 flex justify-center">
          <Link to="/listings" className="btn-harbour">
            View All Listings <FontAwesomeIcon icon={faArrowRight} className="ml-3" />
          </Link>
        </motion.div>
      </motion.section >

      {/* Testimonials Section */}
      <section className="bg-harbour-navy text-white">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24"
        >
          <div className="mb-14">
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 mb-5">
              <div className="h-px w-14 bg-harbour-accent" />
              <p className="uppercase tracking-[0.4em] font-medium text-harbour-sky">
                Client Stories
              </p>
              <div className="h-px w-14 bg-harbour-accent" />
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="text-center font-serif text-5xl lg:text-6xl leading-none text-white"
            >
              Trusted By Our Clients
            </motion.h2>
          </div>

          <motion.div variants={fadeUp} className="mx-auto max-w-6xl">
            <Swiper
              modules={[Autoplay]}
              loop
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              spaceBetween={30}
              slidesPerView={1}
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <div className="mx-auto max-w-4xl rounded-[36px] border border-white/20 bg-harbour-blue p-8 shadow-lg md:p-12">
                    <div className="mb-8 flex gap-1 text-harbour-accent">
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon key={i} icon={faStar} />
                      ))}
                    </div>

                    <p className="font-serif text-xl leading-relaxed text-white/90 md:text-2xl">
                      "{testimonial.quote}"
                    </p>

                    <div className="mt-10 flex items-center gap-5 border-t border-white/20 pt-8">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="h-14 w-14 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-harbour-accent text-lg font-semibold text-harbour-navy">
                          {testimonial.name.split(" ").map((word) => word[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg font-medium tracking-wide text-white">
                          {testimonial.name}
                        </h3>
                        <p className="mt-1 text-xs uppercase tracking-[0.3em] text-white/60">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </motion.div>
      </section>

      {/* Useful Tools Section */}
      <section className="border-b border-harbour-border">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24"
        >
          <div className="mb-14">
            <motion.div variants={fadeUp} className="flex items-center gap-4 mb-5">
              <div className="h-px w-14 bg-harbour-accent" />
              <p className="uppercase tracking-[0.4em] font-medium text-harbour-navy">
                Real Estate Resources
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none text-harbour-navy"
            >
              Useful Tools
            </motion.h2>
          </div>

          <motion.div variants={fadeUp} className="grid gap-8 md:grid-cols-3">
            {[
              {
                title: "Mortgage Calculator",
                description: "Plan your purchase with a clear understanding of your budget and monthly costs.",
                image: calc,
                link: "/calculator",
              },
              {
                title: "Market Reports",
                description: "Access valuable insights about pricing trends and your local real estate market.",
                image: report,
                link: "/real-estate-reports",
              },
              {
                title: "Home Evaluation",
                description: "Discover your home's current value with expert market analysis.",
                image: house,
                link: "/evaluation",
              },
            ].map((card, index) => (
              <Link
                key={index}
                to={card.link}
                className="group"
              >
                <div className="relative overflow-hidden rounded-3xl bg-white border border-harbour-border transition-all duration-500 hover:-translate-y-3 hover:shadow-xl">

                  <div className="relative h-[280px] overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>

                  <div className="relative p-8">

                    <div className="absolute -top-7 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-harbour-navy text-white shadow-xl transition duration-500 group-hover:bg-harbour-accent">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </div>

                    <p className="text-xs uppercase tracking-[0.35em] text-harbour-blue">
                      Discover
                    </p>

                    <h3 className="mt-4 font-serif text-3xl text-harbour-navy">
                      {card.title}
                    </h3>

                    <p className="mt-5 text-sm h-14 leading-5 text-harbour-muted line-clamp-3">
                      {card.description}
                    </p>

                    <div className="mt-8 h-px w-full bg-harbour-border" />

                    <span className="mt-6 inline-block text-xs uppercase tracking-[0.3em] text-harbour-navy transition group-hover:text-harbour-blue">
                      Explore Tool
                    </span>

                  </div>

                </div>
              </Link>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Contact />
    </>
  );
}
